"use client";

import Image from "next/image";
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { BGTRewardsCard } from "@/components/bgt-rewards-card";
import { ClaimSuccessDialog } from "@/components/claim-success-dialog";
import { StatsCard } from "@/components/stats-card";
import { TVOverlay } from "@/components/tv-overlay";
import { Button } from "@/components/ui/button";
import TokenAmountInput, { type TokenOption } from "@/components/token-amount-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { REWARD_VAULT_AQUABERA_ADDRESS } from "@/constants/contracts";
import { useWriteWithToast } from "@/hooks/use-write-with-toast";
import { useAquaberaVaultData, useVault } from "@/hooks/vault";
import { usePrice } from "@/hooks/haiku";
import { cn, formatToken, formatTokenIntelligent } from "@/utils";

// LP Token address for WBERA-HENLO pool
const LP_TOKEN_ADDRESS = "0x04fD6a7B02E2e48caedaD7135420604de5f834f8";

const DEPOSIT_TOKEN_OPTIONS: TokenOption[] = [
  { value: "bera", label: "BERA", icon: "/token/bera.png", iconAlt: "BERA" },
  { value: "wbera", label: "WBERA", icon: "/token/bera.png", iconAlt: "WBERA" },
];

const WITHDRAW_TOKEN_OPTIONS: TokenOption[] = [
  { value: "lp", label: "WBERA-HENLO LP", icon: "/token/henlo.webp", iconAlt: "LP" },
];

const VaultNew = () => {
  const router = useRouter();

  // State for claim success dialog
  const [showClaimSuccessDialog, setShowClaimSuccessDialog] = useState(false);
  const [claimedBGTAmount, setClaimedBGTAmount] = useState<string>("");
  const [claimTxHash, setClaimTxHash] = useState<string | undefined>();

  // Get real vault data from the Aquabera reward vault
  const {
    stakedBalance,
    formattedStakedBalance,
    formattedEarnedRewards,
    earnedRewards,
    apr,
    tvl,
    depositors,
    hasRewards,
    isLoadingBalance,
    isLoadingApi,
    isLoadingRewards,
    isClaiming,
    handleClaimRewards: originalHandleClaimRewards,
    refetchBalance,
  } = useAquaberaVaultData({
    onClaimSuccess: (hash) => {
      setClaimTxHash(hash);
      setShowClaimSuccessDialog(true);
    },
  });

  const {
    // State
    amount,
    selectedToken,
    activeTab,
    autoStake,
    setAmount,
    setSelectedToken,
    setActiveTab,
    setAutoStake,

    // Computed values
    isOnBerachain,
    isTransactionInProgress,
    hasSufficientFunds,
    needsApproval,

    // Balances
    beraBalance,
    wberaBalance,
    lpBalance,

    // Loading states
    isDepositing,
    isWithdrawing,
    isApprovalLoading,
    isWberaApprovalLoading,

    // Handlers
    handleAmountChange,
    handleMaxAmount,
    handleDeposit,
    handleWithdraw,
    handleApprove,
    handleWberaApprove,
    handleSwitchNetwork,
    login,
    address,

    // Manual staking functions
    writeApprove,
    writeStake,
    refetchLpBalance,
    refetchLpAllowanceForRewardVault,
  } = useVault();

  const selectedDepositTokenOption =
    DEPOSIT_TOKEN_OPTIONS.find((option) => option.value === selectedToken) ?? DEPOSIT_TOKEN_OPTIONS[0];

  const selectedDepositBalance = (() => {
    if (!address) return null;

    if (selectedToken === "bera") {
      return beraBalance?.value ?? 0n;
    }

    return wberaBalance ?? 0n;
  })();

  // Inline staking toasts + tx tracking
  // Custom claim handler to snapshot amount before claiming
  const handleClaimRewards = useCallback(async () => {
    if (!address || !earnedRewards || earnedRewards === 0n) return;
    const claimedAmount = formatTokenIntelligent(earnedRewards, 18, true);
    setClaimedBGTAmount(claimedAmount);
    await originalHandleClaimRewards();
  }, [address, earnedRewards, originalHandleClaimRewards]);

  // Get BGT and BERA price for USD calculation
  const { data: beraPrice } = usePrice({ symbol: "BERA" });
  const BGT_PRICE_USD = beraPrice?.usd || null;

  // Dialog handlers
  const handleBoostClick = useCallback(() => {
    setShowClaimSuccessDialog(false);
    // Open Berachain hub boost page in new tab
    window.open('https://hub.berachain.com/boost/0xa0c673180d97213c1c35fe3bf4e684dd3534baab235a106d1f71b9c8a37e4d37a056d47546964fd075501dff7f76aeaf/', '_blank');
  }, []);

  const handleSkipClick = useCallback(() => {
    setShowClaimSuccessDialog(false);
  }, []);

  const [_inlineStakeAmount, setInlineStakeAmount] = useState<bigint | null>(null);
  const inlineStakeApproveTx = useWriteWithToast({
    title: "Approve LP for staking",
  });
  const inlineStakeTx = useWriteWithToast({
    title: "Stake LP tokens",
    onSuccess: async () => {
      await refetchLpBalance();
      setInlineStakeAmount(null);
    },
  });

  // Format numbers for display
  const formatCompactNumber = (num: number): string => {
    if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toFixed(0);
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-6xl overflow-y-auto scrollbar-hide">
      <div className="relative mx-auto mt-8 md:mt-16 p-4 font-inter scrollbar-hide">
        {/* Main Layout with UI and Stats */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left Side - Deposit/Withdrawal UI */}
          <div
            className="relative flex-1 rounded-md p-4"
            style={{
              background:
                "linear-gradient(145deg, #1a2e1c 0%, #0f1f11 30%, #0a1a0c 70%, #0a0a0b 100%)",
              boxShadow: `
                0 12px 40px rgba(28, 199, 94, 0.2),
                0 4px 12px rgba(28, 199, 94, 0.15),
                inset 0 2px 2px rgba(255, 255, 255, 0.15),
                inset 0 -2px 2px rgba(0, 0, 0, 0.8),
                0 0 0 1px rgba(28, 199, 94, 0.2)
              `.trim(),
              border: "1px solid rgba(28, 199, 94, 0.3)",
            }}
          >
            {/* Outer highlight for raised effect */}
            <div
              className="pointer-events-none absolute inset-0 rounded-md"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 40%)",
                opacity: 0.6,
              }}
            />
            {/* Inner bezel frame */}
            <div
              className="pointer-events-none absolute inset-[2px] rounded"
              style={{
                background:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.03) 100%)",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.5)",
              }}
            />

            {/* Vault Logo - Absolute positioned like incinerator */}
            <div className="absolute -top-8 left-0 z-10 flex items-center gap-2">
              <div className="relative h-16 w-48">
                <Image
                  src="/lobby/vault.svg"
                  alt="VAULT"
                  fill
                  className="object-contain drop-shadow-[0_2px_4px_rgba(28,199,94,0.8)]"
                />
              </div>
            </div>

            {/* Title and Info Section */}
            <div className="relative z-10 mt-6">
              {/* Educational Banner */}
              <div className="relative mb-4 overflow-hidden rounded-md">
                <div className="relative z-10 px-4 py-3">
                  <div className="flex flex-col gap-0 leading-tight">
                    <p className="font-pressStart2P text-[9px] uppercase tracking-wider text-white">
                      DEPOSIT BERA OR WBERA INTO
                    </p>
                    <p className="font-pressStart2P text-[9px] uppercase tracking-wider text-white">
                      AQUABERA LP VAULTS.
                    </p>
                    <p className="font-pressStart2P text-[9px] uppercase tracking-wider text-muted-foreground">
                      BGT REWARDS WERE DISCONTINUED IN FEBRUARY 2026.
                    </p>
                  </div>
                </div>
              </div>

              {/* Balance and Rewards Panel */}
              <div
                className="relative overflow-hidden rounded-t-md p-1"
                style={{
                  background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
                  boxShadow: `
                    inset 0 3px 12px rgba(0, 0, 0, 0.9),
                    inset 0 -2px 4px rgba(0, 0, 0, 0.7),
                    inset 0 1px 0 rgba(0, 0, 0, 1),
                    0 0 20px -5px rgba(28, 199, 94, 0.3)
                  `.trim(),
                  border: "2px solid rgba(28, 199, 94, 0.3)",
                }}
              >
                <TVOverlay intensity="medium" />

                {/* Earn Bear Character - Absolute positioned top right */}
                <div
                  className="absolute -bottom-6 right-4 z-20 size-36"
                  style={{
                    filter: `
                      drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))
                      drop-shadow(0 0 20px rgba(28, 199, 94, 0.3))
                    `.trim(),
                  }}
                >
                  <Image src="/poku/earn.png" alt="Earn Bear" fill sizes="144px" className="object-contain" />
                </div>

                {/* User Stats */}
                <div className="relative z-10 p-4">
                  <h2
                    className="mb-4 font-pressStart2P text-xs uppercase text-white"
                    style={{
                      textShadow: "2px 2px 0px #000",
                    }}
                  >
                    Your Position
                  </h2>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-pressStart2P text-[8px] uppercase text-white/60">Staked LP</p>
                      <p className="text-green-400 font-pressStart2P text-lg">
                        {address && !isLoadingBalance ? formattedStakedBalance : "0"}
                      </p>
                      <p className="font-pressStart2P text-[7px] uppercase text-white/40">Staked</p>
                    </div>
                    <div>
                      <p className="font-pressStart2P text-[8px] uppercase text-white/60">Unstaked LP</p>
                      <p className="text-yellow-400 font-pressStart2P text-lg">
                        {address && lpBalance ? formatToken(lpBalance) : "0.00"}
                      </p>
                      <p className="font-pressStart2P text-[7px] uppercase text-white/40">Available</p>
                      {/* Inline Stake Button */}
                      {address && lpBalance !== undefined && lpBalance > 0n && (
                        <Button
                          variant="action-green"
                          size="sm"
                          className="mt-2 h-8 w-full text-[10px]"
                          onClick={async () => {
                            try {
                              const allowanceData = await refetchLpAllowanceForRewardVault();
                              const currentAllowance = allowanceData.data || 0n;
                              setInlineStakeAmount(lpBalance);
                              if (currentAllowance < lpBalance) {
                                await inlineStakeApproveTx.run(() =>
                                  writeApprove({
                                    address: LP_TOKEN_ADDRESS,
                                    args: [
                                      REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
                                      lpBalance,
                                    ],
                                  }),
                                );
                              }
                              await inlineStakeTx.run(() =>
                                writeStake({
                                  address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
                                  args: [lpBalance],
                                }),
                              );
                            } catch (_error) {}
                          }}
                          disabled={
                            isTransactionInProgress ||
                            inlineStakeApproveTx.isLoading ||
                            inlineStakeTx.isLoading
                          }
                        >
                          Stake
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* BGT Rewards Section - Always shown */}
              <BGTRewardsCard
                rewards={earnedRewards}
                formattedRewards={formattedEarnedRewards}
                hasRewards={hasRewards}
                isClaiming={isClaiming}
                onClaim={handleClaimRewards}
                variant="green"
                isConnected={!!address}
                onConnect={login}
              />
            </div>

            {/* Main Deposit/Withdrawal Content */}
            <div
              className="relative overflow-hidden rounded-b-md bg-black"
              style={{
                background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
                boxShadow:
                  "inset 0 2px 8px rgba(0, 0, 0, 0.8), inset 0 -1px 0 rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(0, 0, 0, 0.8)",
              }}
            >
              <TVOverlay intensity="heavy" />

              <div className="relative z-10 p-8">
                {/* Enhanced Tabs for Deposit/Withdraw */}
                <Tabs
                  value={activeTab}
                  onValueChange={(value) => {
                    setActiveTab(value as "deposit" | "withdraw");
                    setAmount("");
                  }}
                  className="w-full"
                >
                  <TabsList
                    variant="vault"
                    className="mb-4 w-full"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(15, 23, 42, 0.65) 0%, rgba(10, 10, 11, 0.85) 55%, rgba(5, 8, 7, 0.95) 100%)",
                      boxShadow:
                        "inset 0 2px 4px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(34, 197, 94, 0.12)",
                    }}
                  >
                    <TabsTrigger
                      variant="vault"
                      value="deposit"
                      style={
                        activeTab === "deposit"
                          ? {
                              background: `linear-gradient(90deg, #1cc75e 0%, #7FE88D 50%, #1cc75e 100%)`,
                              color: "#000",
                              boxShadow: `0 0 20px rgba(28, 199, 94, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
                              textShadow: "1px 1px 0px rgba(255, 255, 255, 0.3)",
                            }
                          : {}
                      }
                    >
                      Deposit
                    </TabsTrigger>
                    <TabsTrigger
                      variant="vault"
                      value="withdraw"
                      style={
                        activeTab === "withdraw"
                          ? {
                              background: `linear-gradient(90deg, #1cc75e 0%, #7FE88D 50%, #1cc75e 100%)`,
                              color: "#000",
                              boxShadow: `0 0 20px rgba(28, 199, 94, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
                              textShadow: "1px 1px 0px rgba(255, 255, 255, 0.3)",
                            }
                          : {}
                      }
                    >
                      Withdraw
                    </TabsTrigger>
                  </TabsList>

                  {/* Deposit Tab Content */}
                  <TabsContent value="deposit" className="mt-0 space-y-6">
                    <TokenAmountInput
                      label="Amount"
                      amount={amount}
                      onAmountChange={handleAmountChange}
                      token={selectedToken}
                      tokenOptions={DEPOSIT_TOKEN_OPTIONS}
                      onTokenChange={(value) => setSelectedToken(value as "bera" | "wbera")}
                      onQuickSelect={handleMaxAmount}
                      balance={selectedDepositBalance}
                    />

                    {/* Auto-stake Checkbox */}
                    <button
                      type="button"
                      onClick={() => setAutoStake(!autoStake)}
                      className="flex w-full items-center gap-3 rounded border border-[#1cc75e]/20 bg-black/40 px-3 py-2 transition-colors duration-200 hover:border-[#1cc75e]/40 hover:bg-[#1cc75e]/5"
                    >
                      {/* Simple checkbox */}
                      <div
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border-2 transition-colors duration-200",
                          autoStake
                            ? "border-[#1cc75e] bg-[#1cc75e]"
                            : "border-[#1cc75e]/50 bg-transparent",
                        )}
                      >
                        {autoStake && (
                          <svg
                            width="12"
                            height="10"
                            viewBox="0 0 12 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 5L4.5 8.5L11 1"
                              stroke="black"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>

                      {/* Labels */}
                      <div className="flex flex-1 flex-col gap-1 text-left">
                        <p className="font-pressStart2P text-[9px] uppercase text-[#1cc75e]">
                          Auto-stake LP tokens
                        </p>
                        <p className="font-pressStart2P text-[8px] uppercase text-white/50">
                          Automatically stake LP tokens after deposit
                        </p>
                      </div>
                    </button>

                    {/* Action Button */}
                    <div className="relative mt-4">
                      {!address ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={login}
                        >
                          {activeTab === "deposit" ? "Sign In to Deposit" : "Sign In to Withdraw"}
                        </Button>
                      ) : !isOnBerachain ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleSwitchNetwork}
                        >
                          Switch to Berachain
                        </Button>
                      ) : activeTab === "withdraw" && needsApproval ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleApprove}
                          disabled={isApprovalLoading || !amount || parseFloat(amount) <= 0}
                        >
                          Approve Withdrawal
                        </Button>
                      ) : activeTab === "deposit" && selectedToken === "wbera" && needsApproval ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleWberaApprove}
                          disabled={isWberaApprovalLoading || !amount || parseFloat(amount) <= 0}
                        >
                          Approve WBERA
                        </Button>
                      ) : (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={activeTab === "deposit" ? handleDeposit : handleWithdraw}
                          disabled={
                            !amount ||
                            parseFloat(amount) <= 0 ||
                            !hasSufficientFunds ||
                            (activeTab === "deposit" ? isDepositing : isWithdrawing)
                          }
                        >
                          {activeTab === "deposit"
                            ? autoStake
                              ? `Deposit ${selectedToken.toUpperCase()} and Stake`
                              : `Deposit ${selectedToken.toUpperCase()}`
                            : "Withdraw for BERA"}
                        </Button>
                      )}
                    </div>
                  </TabsContent>

                  {/* Withdraw Tab Content */}
                  <TabsContent value="withdraw" className="mt-0 space-y-6">
                    {/* Withdraw Amount Input */}
                    <div className="space-y-3">
                      <TokenAmountInput
                        label="Amount"
                        amount={amount}
                        onAmountChange={handleAmountChange}
                        token={WITHDRAW_TOKEN_OPTIONS[0].value}
                        tokenOptions={WITHDRAW_TOKEN_OPTIONS}
                        onQuickSelect={handleMaxAmount}
                        quickActions={[{ label: "MAX", value: 1 }]}
                        balance={address ? stakedBalance : null}
                        readOnlyToken
                      />
                    </div>

                    {/* Action Button for Withdraw */}
                    <div className="relative mt-4">
                      {!address ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={login}
                        >
                          Sign In to Withdraw
                        </Button>
                      ) : !isOnBerachain ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleSwitchNetwork}
                        >
                          Switch to Berachain
                        </Button>
                      ) : needsApproval ? (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleApprove}
                          disabled={isApprovalLoading || !amount || parseFloat(amount) <= 0}
                        >
                          {isApprovalLoading ? "Approving..." : "Approve Withdrawal"}
                        </Button>
                      ) : (
                        <Button
                          className="relative h-16 w-full overflow-hidden bg-gradient-to-b from-[#1cc75e] to-[#17a04b] hover:from-[#2dd56e] hover:to-[#1cb055]"
                          variant="action-green"
                          onClick={handleWithdraw}
                          disabled={
                            !amount ||
                            parseFloat(amount) <= 0 ||
                            !hasSufficientFunds ||
                            isWithdrawing
                          }
                        >
                          {isWithdrawing ? "Withdrawing..." : "Withdraw for BERA"}
                        </Button>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Vault Links - Outside of tabs */}
                <div className="mt-6 flex items-center justify-center gap-4">
                  <a
                    href="https://hub.berachain.com/vaults/0x16e8031814e58f581c8a1d6e087458b5110879fa/"
                    target="_blank"
                    className="flex items-center gap-2 text-xs text-white/60 hover:text-white"
                    rel="noopener"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="40"
                        height="40"
                        rx="10"
                        fill="url(#paint0_radial_6594_43)"
                      ></rect>
                      <g filter="url(#filter0_dd_6594_43)">
                        <path
                          d="M28.2021 23.3959C29.2414 24.7844 29.4593 26.2937 28.8558 27.9238C28.2522 29.554 27.0547 30.6233 25.2631 31.1317C24.4062 31.3749 23.5322 31.3844 22.641 31.1604C21.7497 30.9363 21.0192 30.5124 20.4494 29.8885C18.3827 30.8117 16.441 30.7665 14.6244 29.7529C12.8077 28.7394 11.716 27.0923 11.3494 24.8116C10.4452 24.5912 9.67667 24.157 9.04387 23.5089C8.41106 22.8608 7.95466 22.0435 7.67467 21.0568C7.22521 19.4729 7.58285 17.9897 8.74761 16.607C9.91236 15.2244 11.2639 14.6235 12.8021 14.8042L15.5042 15.1316C15.7951 14.1793 16.2971 13.3284 17.0103 12.5789C17.7235 11.8295 18.566 11.2888 19.538 10.9568L18.9632 8.93157C18.8675 8.59403 18.8986 8.28359 19.0567 8.00025C19.2147 7.71692 19.4626 7.52736 19.8001 7.43157C20.1376 7.33579 20.4481 7.36693 20.7314 7.525C21.0148 7.68308 21.2043 7.93088 21.3001 8.26843L21.9632 10.6053C23.005 10.6183 23.924 10.8414 24.7204 11.2748C25.5167 11.7081 26.2903 12.4074 27.0411 13.3727L29.3001 12.7317C29.6376 12.6359 29.9481 12.667 30.2314 12.8251C30.5148 12.9832 30.7043 13.231 30.8001 13.5685C30.8959 13.9061 30.8647 14.2165 30.7067 14.4999C30.5486 14.7832 30.3008 14.9727 29.9632 15.0685L27.938 15.6433C28.166 16.6447 28.1615 17.642 27.9243 18.6351C27.6871 19.6283 27.2397 20.4987 26.5821 21.2464L28.2021 23.3959ZM18.8084 27.0717C18.6094 26.3706 18.4744 25.6724 18.4034 24.9772C18.3323 24.2819 18.3252 23.5895 18.3821 22.9001C17.8659 23.3551 17.2804 23.7387 16.6255 24.0508C15.9706 24.3629 15.2982 24.5888 14.6084 24.7285C14.8957 25.7411 15.4144 26.4566 16.1644 26.8751C16.9144 27.2935 17.7957 27.359 18.8084 27.0717ZM14.1136 21.5021C14.9445 21.2663 15.6486 20.9543 16.2258 20.5661C16.803 20.1778 17.5095 19.5285 18.3453 18.6179L13.1189 17.9968C12.2775 17.8989 11.6367 18.0878 11.1965 18.5634C10.7564 19.039 10.6357 19.6273 10.8347 20.3284C11.0263 21.0035 11.387 21.4342 11.9168 21.6205C12.4466 21.8068 13.1789 21.7674 14.1136 21.5021ZM24.3789 28.0159C25.028 27.8317 25.4893 27.4553 25.7629 26.8867C26.0364 26.3181 25.9993 25.8166 25.6516 25.3822L22.0453 20.6822C21.7877 21.6531 21.6405 22.5926 21.6037 23.5009C21.5668 24.4091 21.6442 25.2008 21.8357 25.8759C22.0789 26.7327 22.4142 27.346 22.8418 27.7156C23.2693 28.0853 23.7817 28.1854 24.3789 28.0159ZM24.4958 18.6401C24.6818 18.3067 24.7919 17.9038 24.8261 17.4311C24.8603 16.9585 24.8111 16.4885 24.6785 16.0211C24.4427 15.1902 23.9711 14.5666 23.2637 14.1501C22.5564 13.7336 21.8002 13.6395 20.9953 13.8679C20.5279 14.0006 20.1086 14.2037 19.7374 14.4774C19.3662 14.7511 19.0783 15.0712 18.8737 15.4379L22.3095 15.9779L24.4958 18.6401Z"
                          fill="#F1EBE4"
                        ></path>
                      </g>
                      <defs>
                        <filter
                          id="filter0_dd_6594_43"
                          x="5.48694"
                          y="6.37735"
                          width="27.3674"
                          height="27.9442"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          ></feColorMatrix>
                          <feOffset dy="1"></feOffset>
                          <feGaussianBlur stdDeviation="1"></feGaussianBlur>
                          <feComposite in2="hardAlpha" operator="out"></feComposite>
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.45 0"
                          ></feColorMatrix>
                          <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_6594_43"
                          ></feBlend>
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          ></feColorMatrix>
                          <feOffset dy="0.5"></feOffset>
                          <feGaussianBlur stdDeviation="0.5"></feGaussianBlur>
                          <feComposite in2="hardAlpha" operator="out"></feComposite>
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0.909804 0 0 0 0 0.490196 0 0 0 0 0.247059 0 0 0 0.75 0"
                          ></feColorMatrix>
                          <feBlend
                            mode="normal"
                            in2="effect1_dropShadow_6594_43"
                            result="effect2_dropShadow_6594_43"
                          ></feBlend>
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect2_dropShadow_6594_43"
                            result="shape"
                          ></feBlend>
                        </filter>
                        <radialGradient
                          id="paint0_radial_6594_43"
                          cx="0"
                          cy="0"
                          r="1"
                          gradientUnits="userSpaceOnUse"
                          gradientTransform="translate(20 20) rotate(90) scale(20)"
                        >
                          <stop stopColor="#E9D9B2"></stop>
                          <stop offset="1" stopColor="#E6B434"></stop>
                        </radialGradient>
                      </defs>
                    </svg>
                    <span>Berachain Hub</span>
                  </a>
                  <a
                    href="https://app.aquabera.com/vault/0x04fD6a7B02E2e48caedaD7135420604de5f834f8"
                    target="_blank"
                    className="flex items-center gap-2 text-xs text-white/60 hover:text-white"
                    rel="noopener"
                  >
                    <Image
                      src="/vault/aquabera.jpg"
                      alt="Aquabera"
                      width={20}
                      height={20}
                      className="rounded-full"
                    />
                    <span>Aquabera</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Vault Stats */}
          <div
            className="relative h-fit w-full rounded-md p-4 lg:w-80"
            style={{
              background:
                "linear-gradient(145deg, #2e2a1a 0%, #1f1a0f 30%, #1a160a 70%, #0a0a0b 100%)",
              boxShadow: `
                0 12px 40px rgba(255, 204, 0, 0.2),
                0 4px 12px rgba(255, 204, 0, 0.15),
                inset 0 2px 2px rgba(255, 255, 255, 0.15),
                inset 0 -2px 2px rgba(0, 0, 0, 0.8),
                0 0 0 1px rgba(255, 204, 0, 0.2)
              `.trim(),
              border: "1px solid rgba(255, 204, 0, 0.3)",
            }}
          >
            {/* Outer highlight for raised effect */}
            <div
              className="pointer-events-none absolute inset-0 rounded-md"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 40%)",
                opacity: 0.6,
              }}
            />
            {/* Inner bezel frame */}
            <div
              className="pointer-events-none absolute inset-[2px] rounded"
              style={{
                background:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.03) 100%)",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.5)",
              }}
            />

            {/* Title inside the frame */}
            <div className="relative z-10 mb-4 text-center">
              <h2 className="text-yellow-400 font-pressStart2P text-xs uppercase tracking-wider">
                VAULT STATS
              </h2>
            </div>

            {/* Stats Cards - Ordered by importance */}
            <div className="relative z-10 flex flex-col gap-4">
              {/* APR - Most important metric (green) */}
              <StatsCard
                value={isLoadingApi ? "Loading..." : apr > 0 ? `${apr.toFixed(1)}%` : "--"}
                label="Current APR"
                dotColor="#1cc75e"
                textColor="#1cc75e"
                bgColor="#0f2e1c"
                isLoading={isLoadingApi}
              />

              {/* TVL - Key trust metric (yellow for visual interest) */}
              <StatsCard
                value={
                  isLoadingApi ? "Loading..." : tvl > 0 ? `$${formatCompactNumber(tvl)}` : "--"
                }
                label="Total Locked"
                dotColor="#f3c734"
                textColor="#f3c734"
                bgColor="#332d0f"
                isLoading={isLoadingApi}
              />

              {/* Depositors - Community metric (green) */}
              <StatsCard
                value={
                  isLoadingApi
                    ? "Loading..."
                    : depositors > 0
                      ? formatCompactNumber(depositors)
                      : "--"
                }
                label="Depositors"
                dotColor="#1cc75e"
                textColor="#1cc75e"
                bgColor="#072009"
                isLoading={isLoadingApi}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Claim Success Dialog */}
      <ClaimSuccessDialog
        open={showClaimSuccessDialog}
        onOpenChange={setShowClaimSuccessDialog}
        earnedUSD={
          BGT_PRICE_USD && claimedBGTAmount
            ? (parseFloat(claimedBGTAmount) * BGT_PRICE_USD).toFixed(2)
            : "0.00"
        }
        earnedBGT={claimedBGTAmount || "0.00"}
        currentAPR={apr > 0 ? apr.toFixed(2) : undefined}
        txHash={claimTxHash}
        onBoost={handleBoostClick}
        onSkip={handleSkipClick}
      />
    </div>
  );
};

export default VaultNew;
